import React, { useState, useEffect } from 'react'
import { Mail, FileDown, Check, MailCheck } from 'lucide-react'

// Add CSS animations
const dialogStyles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: scale(0.9); }
    to { opacity: 1; transform: scale(1); }
  }
  
  @keyframes bounce {
    0%, 20%, 53%, 80%, 100% {
      transform: translateY(0);
    }
    40%, 43% {
      transform: translateY(-8px);
    }
    70% {
      transform: translateY(-4px);
    }
  }
`

const CustomDownloadDialog = ({
  isOpen,
  onOpenChange,
}: {
  isOpen: boolean
  onOpenChange: (openChange: boolean) => void
}) => {
  const [animationStage, setAnimationStage] = useState(0)

  // Inject styles
  useEffect(() => {
    const styleElement = document.createElement('style')
    styleElement.textContent = dialogStyles
    document.head.appendChild(styleElement)

    return () => {
      document.head.removeChild(styleElement)
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      setAnimationStage(0)
      const stageTimer = setTimeout(() => setAnimationStage(1), 1000)
      const closeTimer = setTimeout(() => {
        setAnimationStage(2)
        setTimeout(() => {
          onOpenChange(false)
        }, 6000)
      }, 3000)

      return () => {
        clearTimeout(stageTimer)
        clearTimeout(closeTimer)
      }
    }
  }, [isOpen, onOpenChange])

  return isOpen ? (
    <div
      className='position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50'
      style={{
        zIndex: 9999,
      }}
    >
      <div
        className='bg-white rounded p-4 mx-auto shadow-lg d-flex flex-column justify-content-center'
        style={{
          width: '28%',
          minHeight: '30%',
          animation: 'fadeIn 0.3s ease-in-out',
        }}
      >
        <div className='d-flex flex-column align-items-center'>
          <div
            className='rounded-circle d-flex align-items-center justify-content-center mb-3'
            style={{
              width: '4rem',
              height: '4rem',
              backgroundColor: 'rgba(var(--bs-primary-rgb), 0.1)',
            }}
          >
            {animationStage < 2 ? (
              <FileDown
                className='text-primary'
                style={{
                  width: '2rem',
                  height: '2rem',
                  animation: 'bounce 1s infinite',
                }}
              />
            ) : (
              <MailCheck
                className='text-success'
                style={{
                  width: '2rem',
                  height: '2rem',
                }}
              />
            )}
          </div>
          <h2 className='h3 fw-bold mb-2 text-dark'>Download Started</h2>
          <div className='d-flex align-items-center' style={{ minHeight: '4rem' }}>
            <p className='text-gray-700 text-center mb-3'>
              {animationStage < 2
                ? "We're preparing your file..."
                : 'The download link will be sent to your email and will also be available in the notification section.'}
            </p>
          </div>
          <div
            className='d-flex align-items-center justify-content-center gap-2'
            style={{
              opacity: animationStage === 1 ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
            }}
          >
            <span
              className='bg-primary rounded-circle d-inline-block'
              style={{
                width: '0.5rem',
                height: '0.5rem',
                animation: animationStage === 1 ? 'bounce 1s infinite' : 'none',
              }}
            ></span>
            <span
              className='bg-primary rounded-circle d-inline-block'
              style={{
                width: '0.5rem',
                height: '0.5rem',
                animation: animationStage === 1 ? 'bounce 1s infinite 0.3s' : 'none',
              }}
            ></span>
            <span
              className='bg-primary rounded-circle d-inline-block'
              style={{
                width: '0.5rem',
                height: '0.5rem',
                animation: animationStage === 1 ? 'bounce 1s infinite 0.6s' : 'none',
              }}
            ></span>
          </div>
        </div>
      </div>
    </div>
  ) : null
}

export default CustomDownloadDialog
